import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { FontAwesome } from "@expo/vector-icons";
import { SafeAreaView } from "react-native-safe-area-context";

import ExploreByCategoryScreenHeader from "../ExploreByCategoryScreenHeader";

import { requestForegroundLocationPermission } from "@/utils/location";
import { RecommendationCategory } from "@/schemas/user/explore";
import { Spacings } from "@/styling/spacings";
import { themes } from "@/Components/Styling/themeConfigs";

type Props = {
  profileCount: number;
  onPostPermissionRequest?: () => void;
};

function NearbyLocationPermissionScreen({
  profileCount,
  onPostPermissionRequest,
}: Props): React.JSX.Element {
  const checkAndSetLocationPermission = async (): Promise<void> => {
    await requestForegroundLocationPermission(
      "Allow Location Access",
      "To see nearby people, turn on location permissions in your device settings.",
    );

    onPostPermissionRequest?.();
  };

  return (
    <LinearGradient
      colors={currentTheme.bg}
      style={styles.gradient}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
    >
      <SafeAreaView style={styles.safeArea} edges={["top"]}>
        <View style={styles.container}>
          <ExploreByCategoryScreenHeader
            category={RecommendationCategory.NEARBY}
            key={RecommendationCategory.NEARBY}
            profileCount={profileCount}
            hideFilterIcon={true}
          />
          <View style={styles.contentContainer}>
            <View style={styles.iconContainer}>
              <FontAwesome name="location-arrow" size={256} color="gray" />
            </View>
            <View style={styles.bottomSectionContainer}>
              <Text style={styles.title}>Allow Location Access</Text>
              <Text style={styles.textBody}>
                Location access lets us show people near you.
              </Text>
              <LinearGradient
                colors={[
                  "#FFB3B3",
                  "#FFD6A5",
                  "#FFFACD",
                  "#C7F9CC",
                  "#A0C4FF",
                  "#BDB2FF",
                  "#FFC6FF",
                ]}
                style={styles.rainbowLine}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
              />
              <TouchableOpacity onPress={() => checkAndSetLocationPermission()}>
                <View style={styles.button}>
                  <Text style={styles.buttonText}>
                    Enable Location Permission
                  </Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );
}

const currentTheme = themes.morning;

const styles = StyleSheet.create({
  gradient: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
    backgroundColor: "transparent",
  },
  container: {
    flex: 1,
    marginHorizontal: Spacings.s1,
  },
  contentContainer: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundColor: "transparent",
    alignItems: "center",
    paddingTop: Spacings.s6,
    paddingHorizontal: Spacings.s6,
  },
  iconContainer: {
    paddingVertical: Spacings.s6,
  },
  bottomSectionContainer: {
    alignItems: "center",
    width: "100%",
    paddingBottom: 3 * Spacings.s6,
  },
  title: {
    fontSize: currentTheme.typography.h1.size.mobile,
    color: currentTheme.typography.h1.color,
    fontWeight: 500,
    marginBottom: Spacings.s3,
  },
  textBody: {
    fontSize: currentTheme.typography.body.size.mobile + 3,
    color: currentTheme.typography.body.color,
    fontWeight: 500,
  },
  rainbowLine: {
    height: 2,
    borderRadius: 2,
    marginVertical: Spacings.s5,
    width: "100%",
  },
  button: {
    borderRadius: 5,
    paddingHorizontal: 2 * Spacings.s4,
    paddingVertical: Spacings.s3,
    backgroundColor: currentTheme.button.color.primary,
  },
  buttonText: {
    fontSize: currentTheme.typography.h2.size.mobile,
    color: currentTheme.button.textColor.primary,
    fontWeight: "bold",
  },
});

export default NearbyLocationPermissionScreen;
